import { useEffect, useEffectEvent, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, Download, ExternalLink, FileText, Image, File } from 'lucide-react'
import { useDocumento, useObtenerUrlDocumento, useDescargarDocumento } from '@/core/hooks/useDocumentos'
import { formatFileSize } from '@/types/documentos'
import { toast } from 'sonner'
import { abrirEnlaceFirmado } from '@/lib/abrir-enlace'

interface ComprobanteViewerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentoId: string | null
}

export function ComprobanteViewer({
  open,
  onOpenChange,
  documentoId,
}: ComprobanteViewerProps) {
  const [url, setUrl] = useState<string | null>(null)

  const { data: documento, isLoading: cargandoDocumento } = useDocumento(documentoId || '')
  const obtenerUrl = useObtenerUrlDocumento()
  // Instancia propia: su isPending no desmonta el iframe, que al volver pediría la URL vencida.
  const obtenerUrlApertura = useObtenerUrlDocumento()
  const descargarDocumento = useDescargarDocumento()

  // Obtener URL firmada cuando se abre el dialog
  const syncUrlFromOpen = useEffectEvent(() => {
    if (open && documento?.archivo_path) {
      obtenerUrl.mutateAsync(documento.archivo_path).then(setUrl).catch(() => {
        toast.error('Error al obtener el documento')
        setUrl(null)
      })
    }
    if (!open) {
      setUrl(null)
    }
  })
  useEffect(() => {
    syncUrlFromOpen()
  }, [open, documento?.archivo_path])

  const handleDescargar = async () => {
    if (!documento) return
    try {
      await descargarDocumento.mutateAsync({
        path: documento.archivo_path,
        nombreArchivo: documento.nombre_original,
      })
      toast.success('Descarga iniciada')
    } catch (error) {
      console.error('Error al descargar:', error)
      toast.error('Error al descargar el documento')
    }
  }

  // La URL de la vista previa puede haber vencido: la pestaña nueva pide la suya.
  const handleAbrirExterno = async () => {
    if (!documento) return
    try {
      const estado = await abrirEnlaceFirmado(() => obtenerUrlApertura.mutateAsync(documento.archivo_path))
      if (estado === 'bloqueada') toast.error('El navegador bloqueó la pestaña nueva')
    } catch {
      toast.error('Error al abrir el documento')
    }
  }

  const isLoading = cargandoDocumento || obtenerUrl.isPending
  const isImage = documento?.mime_type?.startsWith('image/')
  const isPdf = documento?.mime_type === 'application/pdf'

  const getFileIcon = () => {
    if (isImage) return <Image className="h-16 w-16 text-blue-500" />
    if (isPdf) return <FileText className="h-16 w-16 text-red-500" />
    return <File className="h-16 w-16 text-muted-foreground" />
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Comprobante de Pago</DialogTitle>
          <DialogDescription>Vista previa del comprobante adjunto al movimiento</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !documento ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <File className="h-16 w-16 mb-4 opacity-50" />
              <p>Documento no encontrado</p>
            </div>
          ) : (
            <>
              {/* Info del documento */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                {getFileIcon()}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{documento.nombre}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {documento.nombre_original}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(documento.archivo_size)}
                  </p>
                </div>
              </div>

              {/* Preview */}
              {url && (
                <div className="border rounded-lg overflow-hidden bg-muted/30">
                  {isImage && (
                    <img
                      src={url}
                      alt={documento.nombre}
                      className="max-w-full max-h-[400px] mx-auto object-contain"
                    />
                  )}
                  {isPdf && (
                    <iframe
                      src={url}
                      title={documento.nombre}
                      className="w-full h-[400px]"
                    />
                  )}
                  {!isImage && !isPdf && (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      {getFileIcon()}
                      <p className="mt-4">
                        Vista previa no disponible para este tipo de archivo
                      </p>
                      <p className="text-sm">Descarga el archivo para verlo</p>
                    </div>
                  )}
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleAbrirExterno}
                  disabled={!url}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Abrir en nueva pestaña
                </Button>
                <Button
                  onClick={handleDescargar}
                  disabled={descargarDocumento.isPending}
                >
                  {descargarDocumento.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Descargar
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
